import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Box,
  Typography,
  IconButton,
  Fab,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Feedback as FeedbackIcon,
  BugReport,
  EmojiObjects,
  Message,
  StarBorder,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config';

const FeedbackModal = () => {
  const [open, setOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('general');
  const [content, setContent] = useState('');
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const theme = useTheme();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSubmitStatus(null);
    setContent('');
  };

  const getFeedbackIcon = (type) => {
    switch (type) {
      case 'bug':
        return <BugReport fontSize="small" />;
      case 'feature_request':
        return <EmojiObjects fontSize="small" />;
      case 'satisfaction':
        return <StarBorder fontSize="small" />;
      default:
        return <Message fontSize="small" />;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getApiUrl()}/feedback`,
        {
          feedback_type: feedbackType,
          content,
          rating: feedbackType === 'satisfaction' ? rating : null,
          page_url: window.location.pathname,
          browser_info: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSubmitStatus('success');
      setTimeout(handleClose, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Give Feedback" placement="left">
        <Fab
          color="primary"
          size="medium"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            zIndex: 1000,
            boxShadow: theme.shadows[4],
            '&:hover': {
              transform: 'scale(1.1)',
              transition: 'transform 0.2s',
            },
          }}
        >
          <FeedbackIcon />
        </Fab>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: theme.palette.primary.main,
            color: 'white',
            py: 2.5,
            position: 'relative',
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Share Your Feedback
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              '&:hover': {
                background: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 2 }}>
          {submitStatus === 'success' ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h5" color="success.main" gutterBottom fontWeight={600}>
                Thank you for your feedback!
              </Typography>
              <Typography color="text.secondary" variant="body1">
                We appreciate your input and will use it to improve AutoBlogPilot.
              </Typography>
            </Box>
          ) : submitStatus === 'error' ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h5" color="error.main" gutterBottom fontWeight={600}>
                Oops! Something went wrong.
              </Typography>
              <Typography color="text.secondary" variant="body1">
                Please try submitting your feedback again.
              </Typography>
            </Box>
          ) : (
            <>
              <FormControl 
                fullWidth 
                sx={{ 
                  mb: 3,
                  mt: 1,
                }}
              >
                <InputLabel id="feedback-type-label">Feedback Type</InputLabel>
                <Select
                  labelId="feedback-type-label"
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                  label="Feedback Type"
                >
                  {[
                    { value: 'general', label: 'General Feedback' },
                    { value: 'bug', label: 'Report a Bug' },
                    { value: 'feature_request', label: 'Feature Request' },
                    { value: 'satisfaction', label: 'Satisfaction Rating' },
                  ].map((option) => (
                    <MenuItem 
                      key={option.value} 
                      value={option.value}
                      sx={{
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      {getFeedbackIcon(option.value)}
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {feedbackType === 'satisfaction' && (
                <Box 
                  sx={{ 
                    mb: 3, 
                    p: 2, 
                    textAlign: 'center',
                    background: 'rgba(0,0,0,0.02)',
                    borderRadius: 2,
                  }}
                >
                  <Typography component="legend" gutterBottom>
                    How would you rate your experience?
                  </Typography>
                  <Rating
                    value={rating}
                    onChange={(event, newValue) => {
                      setRating(newValue);
                    }}
                    size="large"
                    sx={{
                      '& .MuiRating-iconFilled': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                rows={4}
                label={
                  feedbackType === 'bug'
                    ? 'Describe the bug'
                    : feedbackType === 'feature_request'
                    ? 'Describe the feature you\'d like'
                    : 'Your feedback'
                }
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={
                  feedbackType === 'bug'
                    ? 'What happened? What did you expect to happen?'
                    : feedbackType === 'feature_request'
                    ? 'What feature would make AutoBlogPilot better for you?'
                    : 'Share your thoughts with us'
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(0,0,0,0.02)',
                  },
                }}
              />
            </>
          )}
        </DialogContent>

        {!submitStatus && (
          <DialogActions 
            sx={{ 
              px: 3, 
              pb: 3,
              gap: 1,
            }}
          >
            <Button 
              onClick={handleClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  background: 'rgba(0,0,0,0.05)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!content.trim() || submitting}
              sx={{
                px: 4,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Submit Feedback
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default FeedbackModal; 